// Caches in-progress, not-yet-committed annotation strokes (currently: pen/ink
// only) so they can be offered back to the user if they close the document or
// switch tools before the normal close-button/tool-change commit fires.
//
// One slot per (user, project, document, page) — only the latest draft for
// that page matters, so each new snapshot overwrites the last. A draft is
// cleared the moment its session actually commits (see draftSessionId), so a
// leftover entry on reopen always means "this never made it into Automerge."

const STORAGE_PREFIX = "folia-draft";
// Textbox content is cached per-keystroke and keyed by its own stable
// annotation id (not just page) — unlike pen/shapes/markup, several textboxes
// can be mid-edit on the same page at once, each needing its own slot.
const TEXTBOX_STORAGE_PREFIX = "folia-draft-textbox";
const MAX_DRAFT_AGE_MS = 48 * 60 * 60 * 1000; // stale drafts stop being offered after 48h

function draftKey({ userId, projectId, documentId, pageNumber }) {
  return `${STORAGE_PREFIX}:${userId}:${projectId}:${documentId}:${pageNumber}`;
}

function textboxDraftKeyPrefix({ userId, projectId, documentId, pageNumber }) {
  return `${TEXTBOX_STORAGE_PREFIX}:${userId}:${projectId}:${documentId}:${pageNumber}:`;
}

function textboxDraftKey(args) {
  return `${textboxDraftKeyPrefix(args)}${args.annotationId}`;
}

function readEntry(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function useAnnotationDraftCache() {
  function saveDraft({ userId, projectId, documentId, pageNumber, draftSessionId, rotation, annotations }) {
    const key = draftKey({ userId, projectId, documentId, pageNumber });
    try {
      if (!annotations || annotations.length === 0) {
        localStorage.removeItem(key);
        return;
      }
      localStorage.setItem(
        key,
        JSON.stringify({ draftSessionId, rotation, annotations, updatedAt: Date.now() }),
      );
    } catch (e) {
      // Best-effort only — never let cache persistence block drawing.
    }
  }

  function getDraft({ userId, projectId, documentId, pageNumber }) {
    const key = draftKey({ userId, projectId, documentId, pageNumber });
    const entry = readEntry(key);
    if (!entry?.updatedAt || Date.now() - entry.updatedAt > MAX_DRAFT_AGE_MS) {
      if (entry) try { localStorage.removeItem(key); } catch (e) {}
      return null;
    }
    return entry;
  }

  // Clears the cached draft for a page, but only if it still belongs to
  // draftSessionId — guards against wiping a newer, still-unsaved draft that
  // happened to land in the same slot while an older session's write resolved.
  function clearDraft({ userId, projectId, documentId, pageNumber, draftSessionId }) {
    const key = draftKey({ userId, projectId, documentId, pageNumber });
    try {
      if (draftSessionId) {
        const entry = readEntry(key);
        if (!entry || entry.draftSessionId !== draftSessionId) return;
      }
      localStorage.removeItem(key);
    } catch (e) {
      // ignore
    }
  }

  // Textbox drafts are cached unconditionally on every keystroke (debounced —
  // see FoliaTextBoxAnnotation) and pushed to Automerge unconditionally on the
  // next page load, rather than only being offered back after reconciling
  // against what's committed — the cache always holds the most-recently-typed
  // state, so overwriting Automerge with it is never a regression.
  function saveTextboxDraft({ userId, projectId, documentId, pageNumber, annotationId, data }) {
    const key = textboxDraftKey({ userId, projectId, documentId, pageNumber, annotationId });
    try {
      if (!data?.text) {
        // Emptied back out / abandoned before ever having real content.
        localStorage.removeItem(key);
        return;
      }
      localStorage.setItem(key, JSON.stringify({ data, updatedAt: Date.now() }));
    } catch (e) {
      // Best-effort only.
    }
  }

  function getTextboxDraftsForPage({ userId, projectId, documentId, pageNumber }) {
    const prefix = textboxDraftKeyPrefix({ userId, projectId, documentId, pageNumber });
    const drafts = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(prefix)) continue;
        const entry = readEntry(key);
        if (!entry?.updatedAt || Date.now() - entry.updatedAt > MAX_DRAFT_AGE_MS) {
          localStorage.removeItem(key);
          continue;
        }
        drafts.push({ key, ...entry });
      }
    } catch (e) {
      // ignore
    }
    return drafts;
  }

  function clearTextboxDraftByKey(key) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // ignore
    }
  }

  return {
    saveDraft,
    getDraft,
    clearDraft,
    saveTextboxDraft,
    getTextboxDraftsForPage,
    clearTextboxDraftByKey,
  };
}
